import { useContext, useEffect, useState } from 'react';
import { OpenApiDefinitionWidget } from '@backstage/plugin-api-docs';
import { useNavigate, useParams } from 'react-router';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import { ApiSpec } from './ApiPortalTypes';
import {
  DevHubAlert,
  DevHubGrid,
  DevHubPaper,
  DevHubButton,
  DevHubDynamicIcon,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';
import {
  ConfigHelper,
  decodeBase64GZip,
  Feature,
} from '@devxdiscover/devhub-common';
import { slugifyHyphen } from '../../util/stringFormat';
import yaml from 'js-yaml';

type OpenApiTag = {
  name: string;
  description?: string;
};

type OpenApiOperation = {
  tags?: string[];
};

type OpenApiPath = {
  [operation: string]: OpenApiOperation;
};

type OpenApiSpec = {
  paths?: { [path: string]: OpenApiPath };
  tags?: OpenApiTag[];
};

/**
 * A component to decode and unzip API specs to render them in the API Portal Catalog.
 * @constructor
 */
const ApiSpecDocs = () => {
  const navigate = useNavigate();
  const { categoryName, specName } = useParams<{
    categoryName: string;
    specName: string;
  }>();
  const { org, loading, error, formattedOrgName } =
    useContext(ApiPortalContext);
  const [currentSpec, setCurrentSpec] = useState<ApiSpec>({} as ApiSpec);
  const [definition, setDefinition] = useState<string>('');
  const [filteredTags, setFilteredTags] = useState<OpenApiTag[]>([]);
  const useRequestInterceptor = ConfigHelper.instance.isFeatureEnabled(
    Feature.API_PORTAL_CORS,
  );
  const corsProxy =
    ConfigHelper.instance.config.getOptionalString('app.corsProxy');

  const filterTags = (openApiSpec: OpenApiSpec): OpenApiTag[] => {
    const usedTags = new Set<string>();

    // Collect all tags used in the paths
    Object.values(openApiSpec.paths || {}).forEach(path => {
      Object.values(path).forEach(operation => {
        if (operation.tags) {
          operation.tags.forEach(tag => usedTags.add(tag));
        }
      });
    });

    // Filter tags to include only those that are used
    return openApiSpec.tags?.filter(tag => usedTags.has(tag.name)) || [];
  };

  useEffect(() => {
    if (org && categoryName && specName) {
      const spec = org.categoriesPaged?.categories
        ?.find(x => slugifyHyphen(x.name) === categoryName)
        ?.apiSpecsPaged?.specs?.find(x => slugifyHyphen(x.title) === specName);

      if (spec) {
        setCurrentSpec(spec);
      }
    }
  }, [org, categoryName, specName]);

  useEffect(() => {
    if (currentSpec && currentSpec.definitionPacked) {
      const specDef =
        decodeBase64GZip(currentSpec.definitionPacked) ??
        `openapi: 3.0.0\ninfo:\n  title: ${
          currentSpec.title ?? currentSpec.name
        }\n  version: 0.0.0\n  description: No API Specification Provided!\n paths: {}\n`;

      setDefinition(specDef);

      const openApiSpec = yaml.load(specDef);
      const tags = filterTags(openApiSpec as OpenApiSpec);
      setFilteredTags(tags);
    }
  }, [currentSpec, currentSpec?.definitionPacked]);

  const handleDownloadSpec = () => {
    if (!definition || !currentSpec?.title) return;

    const filename = `${currentSpec.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_api_spec.yaml`;
    const blob = new Blob([definition], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderSpec = () => {
    return (
      <>
        <DevHubGrid item xs={12}>
          <DevHubPaper
            style={{ overflowX: 'auto', maxWidth: '100%', padding: '16px' }}
          >
            <OpenApiDefinitionWidget
              key={currentSpec?.name}
              definition={{
                // @ts-ignore
                ...yaml.load(definition),
                tags: filteredTags,
              }}
              requestInterceptor={async request => {
                if (useRequestInterceptor) {
                  request.url = `${corsProxy}/${request.url}`;
                }
                return request;
              }}
            />
          </DevHubPaper>
        </DevHubGrid>
      </>
    );
  };

  const handleBackClick = () => {
    navigate(`/api-portals/${org._normalizedName}/${categoryName}/specs`);
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal organization data: {error.message}
      </DevHubAlert>
    );
  }

  const downloadButton = (
    <DevHubButton
      variant="contained"
      color="primary"
      onClick={handleDownloadSpec}
      startIcon={<DevHubDynamicIcon iconName="Assignment" />}
      disabled={!definition || !currentSpec?.title}
    >
      Download Spec
    </DevHubButton>
  );

  return (
    <ApiPortalTabbedPage
      formattedOrgName={formattedOrgName}
      apiManagementContent={renderSpec()}
      handleBackClick={handleBackClick}
      additionalActions={downloadButton}
    />
  );
};

export default ApiSpecDocs;
